import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import { toast as sonnerToast, ExternalToast } from 'sonner';

import { getToastDeduper, TToastDedupeOptions } from './toastDedupe';

type TId = string | number;

/** Extended toast options including dedupe support */
export type TToastOptions = ExternalToast & {
  /** Primary dedupe key (notification ID from server) - also passed to Sonner for stable id */
  id?: TId;
  /** Secondary dedupe key */
  dedupeKey?: string;
  /** Skip dedupe check entirely */
  skipDedupe?: boolean;
};

export type TToast = {
  dismiss: (id?: TId) => void;
  error: (message: string, options?: TToastOptions) => TId;
  success: (message: string, options?: TToastOptions) => TId;
  warning: (message: string, options?: TToastOptions) => TId;
  info: (message: string, options?: TToastOptions) => TId;
};

type TContextType = {
  maxVisibleToasts?: number;
  toast: TToast;
};

const initialToast: TContextType = {
  toast: {
    success: () => '',
    error: () => '',
    warning: () => '',
    info: () => '',
    dismiss: () => '',
  },
  maxVisibleToasts: undefined,
};

const ToasterContext = createContext<TContextType>(initialToast);

export let toast: TToast | undefined;

const useToaster = (): TContextType => {
  const { maxVisibleToasts, toast } = useContext(ToasterContext);

  return { toast, maxVisibleToasts };
};

/**
 * Create a toast function with dedupe support
 */
function createToastFunction(
  level: 'success' | 'error' | 'warning' | 'info',
): (message: string, options?: TToastOptions) => TId {
  return (message: string, options?: TToastOptions): TId => {
    // Extract dedupe-related options
    const { id, dedupeKey, skipDedupe, ...sonnerOptions } = options || {};

    // Skip dedupe check if explicitly requested
    if (!skipDedupe) {
      const dedupeOptions: TToastDedupeOptions = {
        id,
        dedupeKey,
        level,
        message,
      };

      const deduper = getToastDeduper();
      if (!deduper.shouldShow(dedupeOptions)) {
        // Duplicate - return the id if provided, otherwise empty string
        // This allows callers to still dismiss by id if they have one
        return id || '';
      }
    }

    // Show toast via Sonner - pass id for stable toast identification
    const sonnerFn = sonnerToast[level];
    return sonnerFn(message, { ...sonnerOptions, id }) as TId;
  };
}

const ToasterProvider: FC<PropsWithChildren> = ({ children }) => {
  const _toaster: TContextType = {
    toast: {
      dismiss: (id?: TId) => sonnerToast.dismiss(id),
      error: createToastFunction('error'),
      success: createToastFunction('success'),
      warning: createToastFunction('warning'),
      info: createToastFunction('info'),
    },
    maxVisibleToasts: 3,
  };

  // eslint-disable-next-line react-hooks/globals
  toast = useMemo(() => {
    return _toaster.toast;
  }, [_toaster.toast]);

  return (
    <ToasterContext.Provider value={_toaster}>
      {children}
    </ToasterContext.Provider>
  );
};

export { ToasterContext, ToasterProvider, useToaster };
